// web/src/buy.ts —— 购买页：机器码 + 产品多选 + 渠道 → 创建订单 → 展示付款动作

import { initI18n, t, getLang, type Lang } from "./i18n";
import {
  fetchProducts,
  fetchChannels,
  createOrder,
  CreateOrderResponse,
} from "./api";
import { productMark } from "./product-visual";
import { looksLikeImageUrl, qrDataUrl } from "./qr";
import {
  PRODUCTS,
  FREE_LANDING_PAGE,
  FULL_VERSION_PRICE_CNY,
  FULL_VERSION_PRICE_USD,
  formatMinorUnits,
  findProduct,
  isFreeProduct,
  normalizeMachineCode,
  isValidMachineCode,
} from "../../shared/products";
import type { ProductDto, ChannelDto } from "./api";

interface DefaultChannel {
  key: string;
  nameZh: string;
  nameEn: string;
  enabled: boolean;
}

let products: ProductDto[] = [];
let channels: (ChannelDto | DefaultChannel)[] = [];
let backendDown = false;
const selectedProducts = new Set<string>();
let selectedChannel: string | null = null;
const orders: CreateOrderResponse[] = [];

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] as string,
  );
}

function renderProducts(): void {
  const container = document.getElementById("products");
  if (!container) return;
  container.innerHTML = "";
  for (const product of products) {
    const item = document.createElement("div");
    item.className = "product-item";
    item.dataset.key = product.key;
    // 切语言会把整份列表重建，选中状态存在 selectedProducts 里，重建后要还原回去
    if (selectedProducts.has(product.key)) item.classList.add("selected");
    const displayName =
      getLang() === "zh"
        ? product.nameZh || product.nameEn
        : product.nameEn || product.nameZh;
    const hasPrice =
      getLang() === "zh" ? product.priceCny > 0 : product.priceUsd > 0;
    item.innerHTML = `
      <div class="p-head">${productMark(product.key)}<span class="p-name">${escapeHtml(displayName)}</span></div>
      <div class="p-price">${hasPrice ? formatProductPrice(product) : t("home.priceTbd")}</div>
    `;
    item.addEventListener("click", () => {
      const productKey = product.key;
      if (selectedProducts.has(productKey)) {
        selectedProducts.delete(productKey);
        item.classList.remove("selected");
      } else {
        selectedProducts.add(productKey);
        item.classList.add("selected");
      }
    });
    container.appendChild(item);
  }
}

function applyProductFromQuery(): void {
  const key = new URLSearchParams(location.search).get("product");
  if (!key) return;
  // 七款软件都改为「免费版可用 + 完整版可选」，已经没有「整款免费、只能打赏」的产品了。
  // 这段兜底只为旧链接（?product=<某款曾经免费的产品>）不跳到空白页，保留即可。
  if (isFreeProduct(key)) {
    location.replace(FREE_LANDING_PAGE[key] ?? "/products.html");
    return;
  }
  const match = products.find((p) => p.key === key);
  if (!match) return;
  selectedProducts.clear();
  selectedProducts.add(match.key);
  document.querySelectorAll<HTMLElement>(".product-item").forEach((n) => {
    n.classList.toggle("selected", n.dataset.key === match.key);
  });
}

/**
 * 产品卡片上的价格：中文页只写人民币、英文页只写美元。
 * 两边定价政策不同，所以任何一种语言都不把另一种货币展示出来。
 * 后端价格单位是「分」/「美分」，由 formatMinorUnits 换成金额。
 */
function formatProductPrice(product: ProductDto): string {
  return getLang() === "zh"
    ? `¥${formatMinorUnits(product.priceCny)} / 份`
    : `$${formatMinorUnits(product.priceUsd)} USD / app`;
}

/**
 * 价格兜底：后端库里的价格是 0（还没执行改价 SQL）时，用 shared/products.ts 的常量顶上。
 * 这样「改价只改一处常量」在线上库尚未同步时也不会露出空白价格。
 * 后端给了非 0 价格时以后端为准（后台改价仍然有效）。
 */
function withFallbackPrice(product: ProductDto): ProductDto {
  const local = findProduct(product.key);
  return {
    ...product,
    priceCny:
      product.priceCny > 0
        ? product.priceCny
        : local?.priceCny ?? FULL_VERSION_PRICE_CNY,
    priceUsd:
      product.priceUsd > 0
        ? product.priceUsd
        : local?.priceUsd ?? FULL_VERSION_PRICE_USD,
  };
}

/**
 * 前端暂不展示的渠道：爱发电的商品还没上架（流程没走完），所以任何语言下都不给这个渠道。
 * 即使线上库里把 afdian 打开了，这里也不显示。
 * 爱发电流程走通、要恢复展示时，把这个集合清空即可（后端适配器与渠道行都还在）。
 */
const HIDDEN_CHANNEL_KEYS = new Set<string>(["afdian"]);

/**
 * 每个语言的页面只显示一个地区的支付渠道，两套渠道不混着摆（顺序也按这里排）：
 *   中文页 —— 虎皮椒的微信支付、虎皮椒的支付宝、人工确认（国内客户，收人民币）；
 *   英文页 —— 只有 Waffo（海外客户，收美元）。
 * 这张表就是「谁能出现在哪个语言」的唯一依据：表里没有的渠道在任何语言下都不出现
 * （微信支付官方渠道 wechatpay 目前没进表；将来启用它，把 key 加进对应语言即可）。
 */
const CHANNEL_KEYS_BY_LANG: Record<Lang, readonly string[]> = {
  zh: ["xunhupay", "xunhupay_alipay", "manual"],
  en: ["waffo"],
};

/** 当前语言可用的渠道：后端下发的与本地兜底列表都走同一套过滤 */
function channelsForCurrentLang(): (ChannelDto | DefaultChannel)[] {
  const allowedKeys = CHANNEL_KEYS_BY_LANG[getLang()];
  const visibleChannels: (ChannelDto | DefaultChannel)[] = [];
  for (const channelKey of allowedKeys) {
    if (HIDDEN_CHANNEL_KEYS.has(channelKey)) continue; // 前端临时隐藏（爱发电）
    const channel = channels.find((candidate) => candidate.key === channelKey);
    if (!channel) continue;
    if (channel.enabled === false) continue; // 未启用渠道不下发（前端双保险）
    visibleChannels.push(channel);
  }
  return visibleChannels;
}

function renderChannels(): void {
  const container = document.getElementById("channels");
  if (!container) return;
  container.innerHTML = "";
  const visibleChannels = channelsForCurrentLang();

  // 切语言后原来选的渠道可能已经不在这个语言的列表里，先清掉，
  // 免得页面上看不见的渠道被悄悄提交上去
  if (
    selectedChannel &&
    !visibleChannels.some(
      (visibleChannel) => visibleChannel.key === selectedChannel,
    )
  ) {
    selectedChannel = null;
  }

  if (visibleChannels.length === 0) {
    // 该语言下一个渠道都没开时，明确说清楚怎么付款，不留一个空的支付方式框
    const note = document.createElement("p");
    note.className = "hint";
    note.textContent = t("buy.channelUnavailable");
    container.appendChild(note);
    return;
  }

  for (const channel of visibleChannels) {
    const item = document.createElement("div");
    item.className = "channel-item";
    item.dataset.key = channel.key;
    item.textContent = getLang() === "zh" ? channel.nameZh : channel.nameEn;
    if (selectedChannel === channel.key) item.classList.add("selected");
    item.addEventListener("click", () => {
      selectedChannel = channel.key;
      container.querySelectorAll(".channel-item").forEach((node) =>
        node.classList.remove("selected"),
      );
      item.classList.add("selected");
    });
    container.appendChild(item);
  }
}

function showMsg(kind: "error" | "info", text: string): void {
  const el = document.getElementById(`msg-${kind}`);
  if (!el) return;
  el.textContent = text;
  el.className = `msg ${kind} show`;
}

async function submit(e: Event): Promise<void> {
  e.preventDefault();
  showMsg("error", "");
  showMsg("info", "");

  const raw = (document.getElementById("machine-code") as HTMLInputElement)
    .value;
  const machineCode = normalizeMachineCode(raw);
  if (!isValidMachineCode(machineCode)) {
    showMsg("error", t("buy.machineInvalid"));
    return;
  }
  if (selectedProducts.size === 0) {
    showMsg("error", t("buy.noProduct"));
    return;
  }
  if (!selectedChannel) {
    showMsg("error", t("buy.noChannel"));
    return;
  }

  const btn = document.getElementById("submit-btn") as HTMLButtonElement;
  btn.disabled = true;
  showMsg("info", t("buy.creating"));

  orders.length = 0;
  const errors: string[] = [];
  for (const productKey of selectedProducts) {
    try {
      const res = await createOrder({
        machineCode,
        productKey,
        channel: selectedChannel,
      });
      orders.push(res);
    } catch (err) {
      errors.push(
        `${productKey}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
  btn.disabled = false;
  showMsg("info", "");

  if (orders.length === 0) {
    showMsg("error", t("buy.apiError") + errors.join("；"));
    return;
  }
  if (errors.length > 0) {
    showMsg(
      "info",
      `${t("buy.apiError")} ${errors.join("；")}（其余订单已创建）`,
    );
  }
  renderOrders();
}

function renderOrders(): void {
  const card = document.getElementById("result-card");
  const list = document.getElementById("order-list");
  if (!card || !list) return;
  list.innerHTML = "";
  const orderNos: string[] = [];
  for (const o of orders) {
    orderNos.push(o.orderNo);
    const div = document.createElement("div");
    div.className = "order-item";
    const ch = o.checkout;
    let action = "";
    /** form 类渠道：自提交 HTML 要等 innerHTML 写入之后再挂，否则会被整段覆盖掉 */
    let deferredFormHolder: HTMLDivElement | null = null;
    if (ch.type === "redirect" && ch.url) {
      action = `<p><a class="btn secondary" href="${escapeHtml(ch.url)}" target="_blank" rel="noopener">${t("buy.pay")} →</a></p>`;
    } else if (ch.type === "qrcode" && ch.qrcode) {
      // 扫码渠道：后端可能给图片地址（虎皮椒），也可能给 weixin:// 这类文本（微信官方），
      // 文本就在本地画成二维码，不依赖任何第三方二维码服务
      const qrImageSrc = looksLikeImageUrl(ch.qrcode)
        ? ch.qrcode
        : qrDataUrl(ch.qrcode);
      if (qrImageSrc) {
        action = `<p class="muted">${t("buy.scanQr")}</p>
          <p class="pay-qr-wrap"><img class="pay-qr" src="${escapeHtml(qrImageSrc)}" alt="${t("buy.scanQr")}" /></p>`;
        if (ch.url) {
          action += `<p><a class="btn secondary" href="${escapeHtml(ch.url)}" target="_blank" rel="noopener">${t("buy.openPayPage")} →</a></p>`;
        }
      } else {
        // 二维码画不出来（内容异常）时，至少把原文给出来
        action = `<p class="muted">${escapeHtml(ch.qrcode)}</p>`;
      }
    } else if (ch.type === "form" && ch.html) {
      // 支付宝 WAP 表单：注入隐藏容器，自动提交（innerHTML 里的 <script> 不会执行，手动 submit form）
      deferredFormHolder = document.createElement("div");
      deferredFormHolder.style.display = "none";
      deferredFormHolder.innerHTML = ch.html;
      action = `<p class="muted">${t("buy.pay")}…（正在跳转收银台）</p>`;
    }
    // 渠道指引文字：manual（无跳转）这类靠这段说清下一步该做什么
    if (ch.instructions) {
      action += `<p class="muted">${escapeHtml(ch.instructions)}</p>`;
    }
    div.innerHTML = `
      <div class="o-no">${t("buy.orderNo")}: ${escapeHtml(o.orderNo)}</div>
      ${action}
    `;
    if (deferredFormHolder) {
      div.appendChild(deferredFormHolder);
      const holder = deferredFormHolder;
      setTimeout(() => {
        const f = holder.querySelector("form");
        if (f) f.submit();
      }, 150);
    }
    list.appendChild(div);
  }
  // 结果页入口：带上全部订单号；并存 sessionStorage 兜底
  sessionStorage.setItem("pendingOrders", orderNos.join(","));
  const go = document.getElementById("goto-result") as HTMLAnchorElement;
  go.href = `/result.html?orderNo=${encodeURIComponent(orderNos.join(","))}`;
  card.style.display = "block";
  card.scrollIntoView({ behavior: "smooth" });
}

async function main(): Promise<void> {
  initI18n();
  try {
    // 七款软件都能买完整版，所以这里不再过滤任何产品（过滤器保留是为了兼容以后可能重开的打赏流程）
    products = (await fetchProducts())
      .filter((product) => !isFreeProduct(product.key))
      .map(withFallbackPrice);
  } catch {
    backendDown = true;
    products = (PRODUCTS as unknown as ProductDto[])
      .filter((product) => !isFreeProduct(product.key))
      .map(withFallbackPrice);
  }
  try {
    channels = await fetchChannels();
  } catch {
    backendDown = true;
    channels = [
      {
        key: "wechatpay",
        nameZh: t("buy.channel.wechatpay"),
        nameEn: "WeChat Pay (official Native QR, instant license)",
        enabled: true,
      },
      {
        key: "xunhupay",
        nameZh: t("buy.channel.xunhupay"),
        nameEn: "WeChat Pay (XunhuPay QR, instant license)",
        enabled: true,
      },
      {
        key: "xunhupay_alipay",
        nameZh: t("buy.channel.xunhupay_alipay"),
        nameEn: "Alipay (XunhuPay QR, instant license)",
        enabled: true,
      },
      {
        key: "waffo",
        nameZh: t("buy.channel.waffo"),
        nameEn: "Waffo (overseas cards / international, instant license)",
        enabled: true,
      },
      {
        key: "manual",
        nameZh: t("buy.channel.manual"),
        nameEn: "Manual (Taobao / international)",
        enabled: true,
      },
    ];
  }
  renderProducts();
  applyProductFromQuery();
  renderChannels();
  // 价格与渠道都跟语言绑定：切语言时把两份列表按新语言重建一遍
  document.addEventListener("langchange", () => {
    renderProducts();
    renderChannels();
  });
  if (backendDown) {
    const tip = document.getElementById("buy-tip");
    if (tip) tip.textContent = t("buy.backendDown");
  }
  document.getElementById("buy-form")?.addEventListener("submit", submit);
}

main();
